import {tabSession} from '../Auth/session';
import {serverCredential} from '../Auth/credential';
import {SessionChangedError, SessionUnavailableError} from '../Auth/tabSession';
import SOCKET_OPTIONS from '../lib/socket-variables';
// A namespace import: many test files mock currentLogin with only the firm and
// user id getters, and they load this module through ErrorBoundaryO. A named
// import of getCurrentUser would fail at link time in every one of them.
import * as currentLogin from '../lib/picos/currentLogin';

// The www.outlawpractice.com support messenger (Home, Messages, Help), served
// from /support-widget/ in a same-origin iframe. It talks to Chatwoot's public
// Client API directly, so customers reach Support while our servers are down.
// Our API only supplies the signed-in user's identity hash.
const WIDGET_PATH = '/support-widget/';
const SUPPORT_SITE = 'https://support.outlawpractice.com';
// Our API issues an opaque Chatwoot identifier (derived from the firm and user
// on the server, so colleagues cannot compute it) and its HMAC; both are hex.
const HEX64 = /^[0-9a-f]{64}$/;
const RETRY_MS = 60_000;
// identify() waits up to a minute per account for the signed-in user's name to load, checking every 2 seconds.
const PROFILE_WAIT_MS = 2_000;
const PROFILE_DEADLINE_MS = 60_000;
const OVERLAY_SCROLLBAR_WIDTH = 15;

type SupportOpenOptions = {help?: boolean; newConversation?: boolean; article?: string};
type Identity = {identifier: string; hash: string; name: string; email: string; firstName: string};
type Issued = {identifier: string; hash: string};

let frame: HTMLIFrameElement | undefined;
let ready = false;
let opened = false;
// The identity message the widget should hold; re-sent whenever it reports ready.
let pendingIdentity: {identity: Identity | null; forget: boolean} = {identity: null, forget: false};
let identified = '';
let identifying: Promise<void> | undefined;
// Set when identify() is called while a run is in flight: one more run follows
// with the then-current account, so a switch mid-run is never dropped.
let identifyAgain = false;
// The account this tab's session was last seen ready for ('' while locked or signed out).
let sessionAccount = '';
// The account a lock or expiry detached without forgetting; logout still erases it.
let suspended = '';
let retryTimer: ReturnType<typeof setTimeout> | undefined;
// The account identify() is waiting on for a name, and when that wait ends.
let profileWait: {account: string; until: number} | undefined;

/** Keep the launcher clear of the app footer and the content scrollbar. */
function positionSupport() {
    if (!frame) return;
    const footer = document.querySelector('.app-footer')?.getBoundingClientRect();
    const indicatorScrollers = [...document.querySelectorAll<HTMLElement>('.scroll-with-indicator__inner')];
    const content = indicatorScrollers.find(element => {
        const bounds = element.getBoundingClientRect();
        return bounds.width > 0 && bounds.height > 0 && bounds.bottom > 0 && bounds.top < window.innerHeight;
    }) ?? document.querySelector<HTMLElement>('.app__content');
    const contentBounds = content?.getBoundingClientRect();
    const contentRightInset = contentBounds && contentBounds.width > 0 ? Math.max(0, window.innerWidth - contentBounds.right) : 0;
    const measuredScrollbar = content ? Math.max(0, content.offsetWidth - content.clientWidth) :
        Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    // macOS overlay scrollbars draw over the content and take no layout width.
    // Reserve their standard width whenever this app surface is vertically scrollable.
    const scrollbar = measuredScrollbar || (content && content.scrollHeight > content.clientHeight ? OVERLAY_SCROLLBAR_WIDTH : 0);
    const bottom = (footer && footer.height > 0 ? Math.max(0, window.innerHeight - footer.top) : 0) + 5;
    frame.style.right = `${contentRightInset + scrollbar + 5}px`;
    frame.style.bottom = `${bottom}px`;
    frame.style.width = opened ? `${Math.min(552, Math.max(36, window.innerWidth - scrollbar - 10))}px` : '36px';
    frame.style.height = opened ? `${Math.max(36, Math.min(920, window.innerHeight - bottom - 5))}px` : '36px';
}

const currentTheme = () => document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
const post = (message: object) => frame?.contentWindow?.postMessage(message, location.origin);

function sendIdentity() {
    if (ready) post({type: 'outlaw-chat-identity', ...pendingIdentity});
}

/** Create the widget iframe once. */
function mountWidget() {
    if (frame) return;
    frame = document.createElement('iframe');
    frame.title = 'Outlaw Practice support';
    frame.src = WIDGET_PATH;
    frame.referrerPolicy = 'strict-origin-when-cross-origin';
    frame.style.cssText = 'position:fixed;right:20px;bottom:20px;width:36px;height:36px;border:0;z-index:2147483001;background:transparent;color-scheme:normal';
    frame.addEventListener('load', () => post({type: 'outlaw-chat-host-ready', theme: currentTheme()}));
    document.body.append(frame);
    positionSupport();
}

const currentIdentifier = () => {
    const firmId = currentLogin.getCurrentFirmId(), userId = currentLogin.getCurrentUserId();
    return firmId && userId && tabSession.isLocalReady() ? `${firmId}:${userId}` : '';
};

const cacheKey = (account: string) => `chatwoot_identity:${account}`;
const validIssued = (value: any): value is Issued => HEX64.test(value?.identifier) && HEX64.test(value?.hash);

/** The identity our API last issued to this browser for the signed-in account, if any. */
function knownIdentity(account: string): Issued | undefined {
    try {
        const cached = JSON.parse(localStorage.getItem(cacheKey(account)) ?? 'null');
        return validIssued(cached) ? {identifier: cached.identifier, hash: cached.hash} : undefined;
    } catch { return undefined; }
}

/**
 * Ask our API for the hash. Without one, `retry` says whether the API was
 * unreachable (network failure or a 5xx, so try again) or answered with a
 * refusal (for example 404 when the server has no inbox token), where polling
 * every minute would only add load.
 */
async function fetchIdentity(account: string): Promise<{issued?: Issued; retry?: boolean}> {
    let operation;
    try { operation = tabSession.capture(); } catch { return {}; }
    if (`${operation.identity.firmId}:${operation.identity.userId}` !== account) return {};
    try {
        // This tab's own credentials, renewed when they are about to expire.
        const credential = serverCredential(await tabSession.credentials(operation));
        const base = `${SOCKET_OPTIONS.secure ? 'https' : 'http'}://${SOCKET_OPTIONS.hostname}`;
        const response = await fetch(new URL('/support/identity', base), {
            method: 'POST', headers: {Authentication: `Bearer ${credential}`}, cache: 'no-store', credentials: 'omit',
            signal: AbortSignal.any([operation.signal, AbortSignal.timeout(15000)]),
        });
        operation.assertCurrent();
        if (!response.ok) return {retry: response.status >= 500};
        const value = await response.json();
        operation.assertCurrent();
        // The server echoes the account it authenticated (firmId:userId from the
        // JWT). The opaque identifier cannot be checked here, so the echo binds the
        // response to this tab's account: a token from another firm or user is refused.
        if (value?.account !== account || !validIssued(value)) return {};
        return {issued: {identifier: value.identifier, hash: value.hash}};
    } catch (error) {
        // A locked, signed-out or switched session is not an outage: the session
        // listener below identifies again once this tab is signed in.
        if (error instanceof SessionChangedError || error instanceof SessionUnavailableError) return {};
        return {retry: true};
    }
}

/** Hand the signed-in user to the widget, which personalizes and verifies the chat contact. */
function attach(account: string, {identifier, hash}: Issued) {
    const user = currentLogin.getCurrentUser(), contact = currentLogin.getCurrentUserContact();
    const first = contact?.first?.trim() ?? '';
    const name = [first, contact?.last?.trim()].filter(Boolean).join(' ') || user?.email || '';
    pendingIdentity = {identity: {identifier, hash, name, email: user?.email ?? '', firstName: first}, forget: false};
    identified = account; suspended = '';
    sendIdentity();
}

/**
 * Attach the signed-in user to the widget. A hash this browser already has
 * authenticates at once, offline included. Our API is then asked for a fresh
 * one, which is cached for the next offline start. With no hash at all
 * (first use while our API is unreachable) the widget stays an unauthenticated
 * session, and this retries when the browser comes back online and, while the
 * API is unreachable, every minute, so the session authenticates once the API
 * answers.
 */
function identify(): Promise<void> {
    if (identifying) { identifyAgain = true; return identifying; }
    identifying = (async () => {
        clearTimeout(retryTimer); retryTimer = undefined;
        const account = currentIdentifier();
        if (!account || account === identified) return;
        // The name comes from the user's contact record, which can load after the
        // login itself (on a reload this first runs at mount). Wait for it so the
        // greeting and Support see the name; after the waits, go on without it.
        const user = currentLogin.getCurrentUser();
        if (!user || (user.contactId && !currentLogin.getCurrentUserContact())) {
            if (profileWait?.account !== account) profileWait = {account, until: Date.now() + PROFILE_DEADLINE_MS};
            if (Date.now() < profileWait.until) {
                retryTimer = setTimeout(() => void identify(), PROFILE_WAIT_MS);
                return;
            }
        }
        profileWait = undefined;
        const known = knownIdentity(account);
        if (known) attach(account, known);
        const {issued, retry} = await fetchIdentity(account);
        if (account !== currentIdentifier()) return;
        if (issued) {
            try { localStorage.setItem(cacheKey(account), JSON.stringify(issued)); } catch { /* storage full or blocked */ }
            if (issued.identifier !== known?.identifier || issued.hash !== known?.hash) attach(account, issued);
        }
        else if (retry && !known) retryTimer = setTimeout(() => void identify(), RETRY_MS);
    })().finally(() => {
        identifying = undefined;
        if (identifyAgain) { identifyAgain = false; void identify(); }
    });
    return identifying;
}

/** Logout: end the signed-in user's chat identity and forget their stored visitor and hash. */
export function resetSupport() {
    clearTimeout(retryTimer); retryTimer = undefined; profileWait = undefined;
    const account = identified || suspended;
    if (account) {
        try { localStorage.removeItem(cacheKey(account)); } catch { /* storage blocked */ }
        pendingIdentity = {identity: null, forget: true};
        sendIdentity();
    }
    identified = ''; suspended = '';
}

/** Show the launcher, and identify the user when signed in. Safe to call often. */
export function initializeSupport() {
    mountWidget();
    void identify();
}

export function openSupport(options: SupportOpenOptions = {}) {
    if (options.article !== undefined) {
        try {
            const url = new URL(options.article, SUPPORT_SITE);
            if (url.origin !== SUPPORT_SITE || url.username || url.password) return;
            // The messenger's Help tab shows the support site; before it is up, a new tab does.
            if (ready) post({type: 'outlaw-chat-open', article: url.href});
            else window.open(url.href, '_blank', 'noopener,noreferrer');
        } catch { /* ignore a malformed article link */ }
        return;
    }
    if (ready) {
        post({type: 'outlaw-chat-open', help: options.help === true, newConversation: options.newConversation === true});
        void identify();
        return;
    }
    // The messenger is not up yet. Open the support site now, while the click
    // still allows a new tab: a tab opened after an await can be blocked as a
    // popup, and navigating this tab would discard the user's unsaved work.
    window.open(SUPPORT_SITE, '_blank', 'noopener,noreferrer');
    initializeSupport();
}

window.addEventListener('message', event => {
    if (!frame?.contentWindow || event.source !== frame.contentWindow || event.origin !== location.origin) return;
    if (event.data?.type === 'outlaw-chat-size') {
        opened = event.data.opened === true;
        positionSupport();
    }
    else if (event.data?.type === 'outlaw-chat-ready') {
        ready = true;
        post({type: 'outlaw-chat-theme', theme: currentTheme()});
        sendIdentity();
    }
});
window.addEventListener('resize', positionSupport);
window.addEventListener('online', () => void identify());
// Sign-in is per tab: other tabs' logins never change this one. When this tab
// locks, expires or switches account, show the anonymous visitor (keeping the
// stored visitor, unlike logout), and identify again once it is signed in.
tabSession.subscribe(() => {
    const account = currentIdentifier();
    if (account === sessionAccount) return;
    sessionAccount = account;
    if (identified && account !== identified) {
        clearTimeout(retryTimer); retryTimer = undefined; profileWait = undefined;
        pendingIdentity = {identity: null, forget: false};
        sendIdentity();
        suspended = identified;
        identified = '';
    }
    if (account && frame) void identify();
});

/** Test-only: forget module state between cases (browser-mode tests share one module instance). */
export const __test__ = {
    reset: () => {
        clearTimeout(retryTimer); retryTimer = undefined;
        frame?.remove(); frame = undefined; ready = false; opened = false;
        pendingIdentity = {identity: null, forget: false};
        identifying = undefined; identifyAgain = false; identified = ''; suspended = ''; profileWait = undefined;
        // The session itself is not reset: track it from where it stands.
        sessionAccount = currentIdentifier();
    },
    frame: () => frame,
};
